import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, Box, Alert } from "@mui/material";
import { BackupExtractJson, FormExtraction, FormExtractions, IdentifyResult } from "../../../interface/returns";
import { ScheduleEPageOneData } from "../../../interface/extracted/schedule-e/default";
import { useFormFieldState } from "../../../hooks/useFormFieldState";
import { useFormConfig } from "../../../hooks/useFormConfig";
import { AppDispatch, RootState } from "../../../configureStore";
import { UPDATE_EXTRACTED_FIELD_VALUE } from "../../../redux/reducer/returns";
import { FormContainer, FormRenderer } from "./shared";

// Form configuration for interface name resolution
type InterfaceNameResolver = (formPageNumber: number) => string;

// Helper to create two-page interface name resolver
const twoPageResolver =
    (baseName: string): InterfaceNameResolver =>
    (pg) =>
        pg === 1 ? `${baseName}PageOneData` : `${baseName}PageTwoData`;

// Helper to create six-page interface name resolver
const sixPageResolver =
    (baseName: string): InterfaceNameResolver =>
    (pg) => {
        const pageNames = ["One", "Two", "Three", "Four", "Five", "Six"];
        return `${baseName}Page${pageNames[pg - 1] || "One"}Data`;
    };

// Helper to create five-page interface name resolver
const fivePageResolver =
    (baseName: string): InterfaceNameResolver =>
    (pg) => {
        const pageNames = ["One", "Two", "Three", "Four", "Five"];
        return `${baseName}Page${pageNames[pg - 1] || "One"}Data`;
    };

// Helper for single-page forms
const singlePageResolver =
    (interfaceName: string): InterfaceNameResolver =>
    () =>
        interfaceName;

// Form type configuration
interface FormTypeConfig {
    reduxKey: keyof FormExtractions;
    getInterfaceName: InterfaceNameResolver;
    usesPageLookup?: boolean; // If true, uses formConfig.pages.find() instead of pages[0]
}

const FORM_CONFIGS: Record<string, FormTypeConfig> = {
    1040: {
        reduxKey: "1040",
        getInterfaceName: twoPageResolver("Form1040"),
        usesPageLookup: true,
    },
    "1040-nr": {
        reduxKey: "1040-nr",
        getInterfaceName: singlePageResolver("Form1040NRPageData"),
        usesPageLookup: true,
    },
    "1040-sr": {
        reduxKey: "1040-sr",
        getInterfaceName: twoPageResolver("Form1040SR"),
        usesPageLookup: true,
    },
    "1040-x": {
        reduxKey: "1040-x",
        getInterfaceName: twoPageResolver("Form1040X"),
        usesPageLookup: true,
    },
    1065: {
        reduxKey: "1065",
        getInterfaceName: sixPageResolver("Form1065"),
        usesPageLookup: true,
    },
    1120: {
        reduxKey: "1120",
        getInterfaceName: sixPageResolver("Form1120"),
        usesPageLookup: true,
    },
    "1120s": {
        reduxKey: "1120s",
        getInterfaceName: fivePageResolver("Form1120S"),
        usesPageLookup: true,
    },
    "1125-e": {
        reduxKey: "1125-e",
        getInterfaceName: singlePageResolver("Form1125EPageData"),
    },
    4562: {
        reduxKey: "4562",
        getInterfaceName: twoPageResolver("Form4562"),
        usesPageLookup: true,
    },
    4684: {
        reduxKey: "4684",
        getInterfaceName: singlePageResolver("Form4684PageData"),
        usesPageLookup: true,
    },
    4868: {
        reduxKey: "4868",
        getInterfaceName: singlePageResolver("Form4868PageData"),
        usesPageLookup: true,
    },
    8582: {
        reduxKey: "8582",
        getInterfaceName: singlePageResolver("Form8582PageData"),
        usesPageLookup: true,
    },
    8825: {
        reduxKey: "8825",
        getInterfaceName: singlePageResolver("Form8825PageData"),
        usesPageLookup: false,
    },
    schedule_1: {
        reduxKey: "schedule_1",
        getInterfaceName: twoPageResolver("Schedule1"),
        usesPageLookup: true,
    },
    schedule_b: {
        reduxKey: "schedule_b",
        getInterfaceName: singlePageResolver("ScheduleBPageData"),
    },
    schedule_c: {
        reduxKey: "schedule_c",
        getInterfaceName: twoPageResolver("ScheduleC"),
        usesPageLookup: true,
    },
    schedule_d: {
        reduxKey: "schedule_d",
        getInterfaceName: twoPageResolver("ScheduleD"),
        usesPageLookup: true,
    },
    schedule_e: {
        reduxKey: "schedule_e",
        getInterfaceName: twoPageResolver("ScheduleE"),
        usesPageLookup: true,
    },
    schedule_f: {
        reduxKey: "schedule_f",
        getInterfaceName: twoPageResolver("ScheduleF"),
        usesPageLookup: true,
    },
    "schedule_k-1_1065": {
        reduxKey: "schedule_k-1_1065",
        getInterfaceName: twoPageResolver("ScheduleK1Form1065"),
        usesPageLookup: true,
    },
    "schedule_k-1_1120s": {
        reduxKey: "schedule_k-1_1120s",
        getInterfaceName: twoPageResolver("ScheduleK1Form1120S"),
        usesPageLookup: true,
    },
    "schedule_m-3_1065": {
        reduxKey: "schedule_m-3_1065",
        getInterfaceName: singlePageResolver("ScheduleM3Form1065PageData"),
        usesPageLookup: true,
    },
    "schedule_m-3_1120": {
        reduxKey: "schedule_m-3_1120",
        getInterfaceName: singlePageResolver("ScheduleM3Form1120PageData"),
        usesPageLookup: true,
    },
    "schedule_m-3_1120s": {
        reduxKey: "schedule_m-3_1120s",
        getInterfaceName: singlePageResolver("ScheduleM3Form1120SPageData"),
        usesPageLookup: true,
    },
    statements: {
        reduxKey: "statements",
        getInterfaceName: singlePageResolver("StatementsPageData"),
    },
    "w-2": {
        reduxKey: "w-2",
        getInterfaceName: singlePageResolver("W2Data"),
    },
};

interface FormDataEntryProps {
    formData: FormExtraction<any>;
    originalPage: number;
    formType: string;
    formIndexOnPage?: number;
}

const FormDataEntry = ({ formData, originalPage, formType, formIndexOnPage = 1 }: FormDataEntryProps) => {
    const config = FORM_CONFIGS[formType];
    const dispatch = useDispatch<AppDispatch>();
    const stubCreatedRef = useRef<Set<string>>(new Set());

    const { reduxKey, getInterfaceName, usesPageLookup } = config ?? {
        reduxKey: "" as keyof FormExtractions,
        getInterfaceName: (() => "") as InterfaceNameResolver,
        usesPageLookup: false,
    };

    const activeExtraction = useSelector<RootState, FormExtractions[typeof reduxKey] | undefined>(
        ({ returns }) => returns.extractedJson?.[reduxKey],
    );
    // Pick the extraction whose (originalPage, formIndexOnPage) matches. Treat missing
    // formIndexOnPage as 1 so legacy single-instance records continue to resolve.
    const matchesInstance = useCallback(
        (e: { extractedData: { originalPage: number }; formIndexOnPage?: number }) =>
            e.extractedData.originalPage === originalPage && (e.formIndexOnPage ?? 1) === formIndexOnPage,
        [originalPage, formIndexOnPage],
    );

    const currentExtraction = useMemo(
        () => activeExtraction?.extraction?.find(matchesInstance),
        [activeExtraction, matchesInstance],
    );

    const extractionIndex = useMemo(
        () => activeExtraction?.extraction?.findIndex(matchesInstance) ?? -1,
        [activeExtraction, matchesInstance],
    );

    const backupJson = useSelector<RootState, BackupExtractJson>(({ returns }) => returns.backupJson);
    const backup = useMemo<IdentifyResult[]>(() => backupJson[reduxKey] ?? [], [backupJson, reduxKey]);
    const formPageNumber = useMemo(
        () =>
            currentExtraction?.extractedData.formPageNumber ??
            backup.find((b) => b.pageNumber === originalPage)?.form?.page ??
            1,
        [currentExtraction, backup, originalPage],
    );

    // Create a stub extraction entry when none exists for this (page, instance)
    useEffect(() => {
        if (extractionIndex !== -1 || !activeExtraction || originalPage === 0) return;
        const stubKey = `${reduxKey}-${originalPage}-${formIndexOnPage}`;
        if (stubCreatedRef.current.has(stubKey)) return;
        stubCreatedRef.current.add(stubKey);

        const newIndex = activeExtraction.extraction?.length || 0;
        dispatch({
            type: UPDATE_EXTRACTED_FIELD_VALUE,
            value: {
                version: "0.0.1",
                type: reduxKey,
                pageNumber: formPageNumber,
                formIndexOnPage,
                overallConfidence: 0,
                requiresManualReview: false,
                extractedData: {
                    page: formPageNumber,
                    originalPage,
                    formPageNumber,
                    taxYear: 0,
                    data: {},
                },
                fieldConfidence: {},
                extractionMetadata: {
                    documentQuality: "N/A",
                    scanQuality: "N/A",
                    extractionNotes: "Stub entry created for manual data entry",
                },
            },
            location: `${reduxKey}.extraction[${newIndex}]`,
            silent: true,
        });
    }, [extractionIndex, activeExtraction, originalPage, formIndexOnPage, reduxKey, formPageNumber, dispatch]);

    const taxYear = currentExtraction?.extractedData?.taxYear;

    const { config: formConfig, loading: configLoading, error: configError } = useFormConfig(formType, taxYear);

    const initialData = useMemo(() => {
        const data = currentExtraction?.extractedData?.data;
        // For statements, the array is directly at data - wrap it to match expected shape
        if (formType === "statements" && Array.isArray(data)) {
            return { statements: data };
        }
        // For 8825, merge all page extractions and transform properties to array
        if (formType === "8825") {
            // Merge data from all extraction pages into one object
            const allPages = activeExtraction?.extraction ?? [];
            const merged = allPages.reduce((acc: any, ext: any) => {
                const pageData = ext?.extractedData?.data;
                if (!pageData) return acc;
                // If either side has properties as an array (from local edits saved to Redux),
                // keep the array — don't spread it into an object which corrupts the data
                const accProps = acc.properties;
                const pageProps = pageData.properties;
                let mergedProperties;
                if (Array.isArray(accProps) || Array.isArray(pageProps)) {
                    mergedProperties = Array.isArray(accProps) ? accProps : pageProps;
                } else {
                    mergedProperties = { ...(accProps || {}), ...(pageProps || {}) };
                }
                return {
                    ...acc,
                    ...pageData,
                    properties: mergedProperties,
                    summary: { ...(acc.summary || {}), ...(pageData.summary || {}) },
                };
            }, {});
            const d = merged;
            if (d?.properties && !Array.isArray(d.properties)) {
                const propertyKeys = [
                    "propertyA",
                    "propertyB",
                    "propertyC",
                    "propertyD",
                    "propertyE",
                    "propertyF",
                    "propertyG",
                    "propertyH",
                ];
                const hasData = (val: any): boolean => {
                    if (val === null || val === undefined || val === "") return false;
                    if (Array.isArray(val)) return val.some(hasData);
                    if (typeof val === "object") return Object.values(val).some(hasData);
                    return true;
                };
                const propertiesArray = propertyKeys
                    .filter((key) => hasData(d.properties[key]))
                    .map((key) => {
                        const prop = d.properties[key];
                        // Flatten line1 sub-fields (address, type, fairRentalDays, personalUseDays) to top level
                        const { line1, ...rest } = prop;
                        return { ...rest, ...(line1 || {}) };
                    });
                // Convert per-property line15 arrays into shared otherExpenses format
                const maxExpenses = Math.max(
                    0,
                    ...propertiesArray.map((p: any) => (Array.isArray(p.line15) ? p.line15.length : 0)),
                );
                const otherExpenses = Array.from({ length: maxExpenses }, (_, expIdx) => ({
                    description:
                        propertiesArray.find((p: any) => p.line15?.[expIdx]?.description)?.line15[expIdx]
                            ?.description || "",
                    amounts: propertiesArray.map((p: any) => p.line15?.[expIdx]?.amount ?? null),
                }));
                // Remove line15 from each property (now stored in otherExpenses)
                const cleanedProperties = propertiesArray.map(({ line15: _line15, ...rest }: any) => rest);
                const properties = cleanedProperties.length > 0
                    ? cleanedProperties
                    : [{ address: "", type: "", fairRentalDays: null, personalUseDays: null }];
                return { ...d, properties, otherExpenses };
            }
            // Properties already an array (from local edits) or missing entirely
            const result = d || {};
            if (!result.properties || (Array.isArray(result.properties) && result.properties.length === 0)) {
                return { ...result, properties: [{ address: "", type: "", fairRentalDays: null, personalUseDays: null }] };
            }
            return result;
        }
        // For schedule_e, convert letter-keyed properties (A, B, C) to array
        if (formType === "schedule_e") {
            const d = data as ScheduleEPageOneData | undefined;
            if (d?.partOne?.properties && !Array.isArray(d.partOne.properties)) {
                const props = d.partOne.properties as unknown as Record<string, Record<string, unknown> | undefined>;
                const propertyKeys = ["A", "B", "C"] as const;
                const hasData = (val: unknown): boolean => {
                    if (val === null || val === undefined || val === "") return false;
                    if (Array.isArray(val)) return val.some(hasData);
                    if (typeof val === "object") return Object.values(val as Record<string, unknown>).some(hasData);
                    return true;
                };
                const propertiesArray = propertyKeys
                    .filter((key) => hasData(props[key]))
                    .map((key) => {
                        const { propertyDetails, ...rest } = props[key]!;
                        return { ...rest, ...((propertyDetails as Record<string, unknown>) || {}) };
                    });
                // Ensure at least one empty property exists so the columnar table renders
                const properties = propertiesArray.length > 0
                    ? propertiesArray
                    : [{ physicalAddress: { street: "", city: "", state: "", zipCode: "" }, typeOfProperty: "", fairRentalDays: null, personalUseDays: null, qjv: false }];
                return {
                    ...d,
                    partOne: { ...d.partOne, properties },
                };
            }
            // Ensure at least one empty entry for all I3/I4 arrays
            const result = d || {} as any;
            const partOne = result.partOne || {};
            const partTwo = result.partTwo || {};
            const partThree = result.partThree || {};

            const emptyProp = { physicalAddress: { street: "", city: "", state: "", zipCode: "" }, typeOfProperty: "", fairRentalDays: null, personalUseDays: null, qjv: false };
            const emptyPartnership = { name: "", columnB: "", isForeignPartnership: false, ein: "", checkColumnE: false, checkColumnF: false, passiveLossAllowed: null, passiveIncomeFromScheduleK1: null, nonpassiveLossAllowed: null, section179Deduction: null, nonpassiveIncomeFromScheduleK1: null };
            const emptyEstate = { name: "", ein: "", passiveDeductionOrLoss: null, passiveIncomeFromScheduleK1: null, deductionOrLossFromScheduleK1: null, otherIncomeFromScheduleK1: null };

            const props = partOne.properties;
            const partnerships = partTwo.lineTwentyEight;
            const estates = partThree.estatesAndTrusts;

            const needsProps = !props || (Array.isArray(props) && props.length === 0);
            const needsPartnerships = !partnerships || (Array.isArray(partnerships) && partnerships.length === 0);
            const needsEstates = !estates || (Array.isArray(estates) && estates.length === 0);

            if (needsProps || needsPartnerships || needsEstates) {
                return {
                    ...result,
                    partOne: { ...partOne, properties: needsProps ? [emptyProp] : props },
                    partTwo: { ...partTwo, lineTwentyEight: needsPartnerships ? [emptyPartnership] : partnerships },
                    partThree: { ...partThree, estatesAndTrusts: needsEstates ? [emptyEstate] : estates },
                };
            }
            return result;
        }
        return data || {};
    }, [currentExtraction?.extractedData?.data, activeExtraction, formType]);

    const formStateOptions = useMemo(
        () => ({
            interfaceName: getInterfaceName(formPageNumber),
            currentPageIndex: extractionIndex,
            formType: formType as any,
        }),
        [getInterfaceName, formPageNumber, extractionIndex, formType],
    );

    const { handleFieldChange, getFieldValue } = useFormFieldState(initialData, formStateOptions);

    const getFieldConfidence = useCallback(
        (fieldName: string) => {
            const confidence = currentExtraction?.fieldConfidence?.[fieldName]?.value;
            return confidence || 1;
        },
        [currentExtraction],
    );

    const pageConfig = useMemo(() => {
        if (!formConfig) return undefined;
        if (usesPageLookup) {
            return formConfig.pages.find((page) => page.pageNumber === formPageNumber);
        }
        return formConfig.pages[0];
    }, [formConfig, formPageNumber, usesPageLookup]);

    if (!config) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 4 }}>
                <Alert severity="error">Unknown form type: {formType}</Alert>
            </Box>
        );
    }

    if (configError) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 4 }}>
                <Alert severity="error">{configError}</Alert>
            </Box>
        );
    }

    if (configLoading || !formConfig) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!pageConfig) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 4 }}>
                <Alert severity="info">No form fields configured for this page.</Alert>
            </Box>
        );
    }

    return (
        <FormContainer
            formName={formConfig.formName}
            formData={formData}
            currentExtraction={currentExtraction}
            formPageNumber={formPageNumber}
            totalPages={formConfig.pages.length}
        >
            <FormRenderer
                pageConfig={pageConfig}
                formType={formType}
                formName={formConfig.formName}
                getFieldValue={getFieldValue}
                handleFieldChange={handleFieldChange}
                getFieldConfidence={getFieldConfidence}
                currentExtraction={currentExtraction}
            />
        </FormContainer>
    );
};

export default FormDataEntry;
